import { type StakingOrigin } from '@capsicum/common';
import { useEffect, useState } from 'react';

import { useFanTokenStakingNavigation } from './useFanTokenStakingNavigation';
import { useFanTokenStakingRouter } from './useFanTokenStakingRouter';

/**
 * @param origin - string Entry point to fan-token-staking. If provided it will be used to navigate back at the end of the flow
 * @param originIds - Array<string> Optional param if entry point url includes ids
 * @description This hook contains all necessary logic needed to redirect to fan-token-staking.
 * If origin param is provided it will be used to redirect back to the entry point at the end of the staking flow.
 *
 * the originIds contains the needed param for the navigation to the origin IN THE CORRECT ORDER
 *
 * Example:
 * /fan-token-staking/{currencyId}/stake?origin={StakingOrigin.VOTES}&originIds={partnerId}&originIds={surveyId}
 *
 * const options = {origin: StakingOrigin.VOTES, originIds: [partnerId, survayId]}
 **/
export const useFanTokenStakingNavigationOnDemand = (options?: {
	origin?: StakingOrigin;
	originIds?: Array<string>;
}) => {
	const { handleNavigationToStake } = useFanTokenStakingRouter();

	const [currencyId, setCurrencyId] = useState<string>('');

	const { isLoading, isStakeAllowed, operationDetails, userCurrentStake, userHasCurrentStake } =
		useFanTokenStakingNavigation(currencyId, options);

	useEffect(() => {
		if (isLoading || !operationDetails || !currencyId.length) return;

		handleNavigationToStake({
			currencyId,
			currentStake: operationDetails?.user.currentStake,
			origin: options?.origin,
			originIds: options?.originIds
		});

		setCurrencyId('');
	}, [operationDetails, isLoading]);

	const navToFanTokenStake = (fanTokenCurrencyId?: string) => {
		if (!fanTokenCurrencyId) return;

		setCurrencyId(fanTokenCurrencyId);
	};

	return {
		isLoading,
		isStakeAllowed,
		navToFanTokenStake,
		userCurrentStake,
		userHasCurrentStake
	};
};
